import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { showToast } from '../components/Toast';
import { getApiUrl, getApiUrlWithDetection } from '../utils/config';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [API_URL, setAPI_URL] = useState(getApiUrl());

  useEffect(() => {
    // Auto-detect API URL on mount
    getApiUrlWithDetection().then(url => setAPI_URL(url));
  }, []);

  useEffect(() => {
    if (!API_URL) return; // Wait for API URL to be detected
    const newSocket = io(API_URL, {
      transports: ['websocket', 'polling']
    });

    newSocket.on('connect', () => {
      console.log('Socket connected');
      setConnected(true);
    });

    newSocket.on('disconnect', () => {
      console.log('Socket disconnected');
      setConnected(false);
    });

    newSocket.on('orderCompleted', () => {
      // Handled per-page where needed
    });

    newSocket.on('orderStatusUpdated', (data) => {
      // Per-page listeners handle this — suppress global toast
      console.log('Order status updated:', data);
    });

    newSocket.on('order_created', () => {
      // Per-page listeners handle this — suppress global toast
    });

    const storedSession = JSON.parse(localStorage.getItem('diningSession') || 'null');
    if (storedSession?.sessionCode && storedSession?.status === 'active') {
      newSocket.emit('joinSessionRoom', {
        sessionCode: storedSession.sessionCode,
        token: storedSession.qrToken,
      });
    }

    const storedParcelSession = JSON.parse(localStorage.getItem('parcelSession') || 'null');
    if (storedParcelSession?.sessionId) {
      newSocket.emit('joinParcelSessionRoom', storedParcelSession.sessionId);
    }

    // Dynamic join on local storage / user session changes
    const handleUserUpdateEvent = () => {
      const stored = JSON.parse(localStorage.getItem('diningSession') || 'null');
      if (stored?.sessionCode && stored?.status === 'active') {
        newSocket.emit('joinSessionRoom', {
          sessionCode: stored.sessionCode,
          token: stored.qrToken,
        });
      }
      const storedParcel = JSON.parse(localStorage.getItem('parcelSession') || 'null');
      if (storedParcel?.sessionId) {
        newSocket.emit('joinParcelSessionRoom', storedParcel.sessionId);
      }
    };
    window.addEventListener('userUpdated', handleUserUpdateEvent);

    newSocket.on('sessionJoined', (data) => {
      console.log('Session room joined:', data.room);
    });

    newSocket.on('sessionEnded', (data) => {
      const stored = JSON.parse(localStorage.getItem('diningSession') || 'null');
      if (!data.sessionCode || data.sessionCode === stored?.sessionCode) {
        localStorage.removeItem('diningSession');
        localStorage.removeItem('diningMember');
        localStorage.removeItem('diningTable');
        window.dispatchEvent(new Event('userUpdated'));
      }
      const storedParcel = JSON.parse(localStorage.getItem('parcelSession') || 'null');
      if (data.sessionId && data.sessionId === storedParcel?.sessionId) {
        localStorage.removeItem('parcelSession');
        localStorage.removeItem('parcelCart');
        window.dispatchEvent(new Event('userUpdated'));
      }
    });

    newSocket.on('paymentUpdate', () => {
      // Suppress global payment toast — customer pages handle this individually
    });

    newSocket.on('waiterCall', () => {
      // Handled per-page where needed
    });

    newSocket.on('cleaningRequest', () => {
      // Handled per-page where needed
    });

    newSocket.on('settingsUpdated', () => {
      window.dispatchEvent(new Event('settingsUpdated'));
    });

    newSocket.on('restaurantStatusUpdated', () => {
      window.dispatchEvent(new Event('restaurantStatusUpdated'));
    });

    newSocket.on('menuUpdated', () => {
      window.dispatchEvent(new Event('menuUpdated'));
    });

    setSocket(newSocket);

    return () => {
      window.removeEventListener('userUpdated', handleUserUpdateEvent);
      newSocket.close();
    };
  }, [API_URL]);

  const joinUserRoom = (userId) => {
    if (socket && userId) {
      socket.emit('joinUserRoom', userId);
    }
  };

  const leaveUserRoom = (userId) => {
    if (socket && userId) {
      socket.emit('leaveUserRoom', userId);
    }
  };

  const joinRoleRoom = (role) => {
    if (socket && role) {
      socket.emit('joinRoleRoom', role);
    }
  };

  const leaveRoleRoom = (role) => {
    if (socket && role) {
      socket.emit('leaveRoleRoom', role);
    }
  };

  const joinSessionRoom = (sessionCode, token) => {
    if (socket && sessionCode) {
      socket.emit('joinSessionRoom', { sessionCode, token });
    }
  };

  const leaveSessionRoom = (sessionCode) => {
    if (socket && sessionCode) {
      socket.emit('leaveSessionRoom', sessionCode);
    }
  };

  const joinParcelSessionRoom = (sessionId) => {
    if (socket && sessionId) {
      socket.emit('joinParcelSessionRoom', sessionId);
    }
  };

  const leaveParcelSessionRoom = (sessionId) => {
    if (socket && sessionId) {
      socket.emit('leaveParcelSessionRoom', sessionId);
    }
  };

  return (
    <SocketContext.Provider value={{ 
      socket, 
      connected, 
      joinUserRoom, 
      leaveUserRoom,
      joinRoleRoom,
      leaveRoleRoom,
      joinSessionRoom,
      leaveSessionRoom,
      joinParcelSessionRoom,
      leaveParcelSessionRoom
    }}>
      {children}
    </SocketContext.Provider>
  );
};
